/**
 * @author    Nikko Renolds
 * @package		Trap Tycoon
 * @link		  https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license		MIT
 * @description Frontend component representing the Street Arena where players can find fights.
 */

import React, { Suspense, useState } from "react";
import { Canvas } from "@react-three/fiber";
import {
  OrbitControls,
  Stars,
  Environment,
  ContactShadows,
} from "@react-three/drei";
import { UserCheck, Bot } from "lucide-react";
import { Thug } from "./models/Thug";

function SceneContents({ isFighting }) {
  // Use 'Scene_Root' or whatever name showed up in your console log
  const currentAnim = isFighting ? "Attack" : "Idle";

  return (
    <>
      <Stars
        radius={100}
        depth={50}
        count={5000}
        factor={4}
        saturation={0}
        fade
        speed={1}
      />
      <ambientLight intensity={0.8} />
      <spotLight
        position={[10, 10, 10]}
        angle={0.15}
        penumbra={1}
        intensity={2}
        castShadow
      />
      <Environment preset="city" />

      <Suspense fallback={null}>
        <Thug animation={currentAnim} position={[0, -1, 0]} scale={1.5} />
      </Suspense>

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]} receiveShadow>
        <planeGeometry args={[20, 20]} />
        <meshStandardMaterial color="#050505" />
      </mesh>

      <ContactShadows
        opacity={0.6}
        scale={10}
        blur={2}
        far={10}
        resolution={256}
        color="#000000"
      />
      <OrbitControls
        enableZoom={false}
        autoRotate={!isFighting}
        autoRotateSpeed={0.5}
      />
    </>
  );
}

export default function Arena({ onFight }) {
  const [isFighting, setIsFighting] = useState(false);

  const handleFightTrigger = () => {
    setIsFighting(true);
    // Return to idle after a few seconds or when the backend returns data
    setTimeout(() => setIsFighting(false), 3000);
    onFight();
  };

  return (
    <div className="space-y-6">
      {/* 3D Visualizer Container */}
      <div className="h-[400px] w-full bg-zinc-950 border-2 border-red-900 relative shadow-[0_0_50px_rgba(153,27,27,0.15)] overflow-hidden">
        <Canvas camera={{ position: [0, 2, 5], fov: 45 }} shadows>
          <Suspense fallback={null}>
            <SceneContents isFighting={isFighting} />
          </Suspense>
        </Canvas>

        {/* Overlay HUD elements from original Arena */}
        <div className="absolute inset-0 pointer-events-none p-6 flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <h2 className="text-4xl font-black italic text-white uppercase tracking-tighter">
              The Street Arena
            </h2>
            <div className="bg-red-900/20 border border-red-900 p-2 text-[10px] text-red-500 font-black uppercase italic">
              Live Combat Network v2.8
            </div>
          </div>
        </div>
      </div>

      {/* Action UI */}
      <div className="bg-zinc-950 border-2 border-red-900 p-8 text-center">
        <p className="text-zinc-500 mb-8 font-mono text-xs uppercase tracking-widest leading-relaxed">
          Find a rival on your level. Win cash and xp, or lose your thugs.
          Visualizing high-fidelity combat resolution...
        </p>

        <button
          onClick={handleFightTrigger}
          className="bg-red-600 hover:bg-red-400 text-black font-black px-12 py-5 text-2xl transition-all active:scale-95 italic uppercase shadow-[0_0_20px_rgba(220,38,38,0.4)]"
        >
          Find Someone to Roll On
        </button>

        <div className="mt-8 flex justify-center gap-8 text-[10px] uppercase font-mono text-zinc-600">
          <span className="flex items-center gap-1">
            <UserCheck size={12} /> Real Players First
          </span>
          <span className="flex items-center gap-1">
            <Bot size={12} /> AI Rivals Guaranteed
          </span>
        </div>
      </div>
    </div>
  );
}
